import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { useSettings } from '../context/SettingsContext';
import { useLanguage } from '../context/LanguageContext';

export default function SettingsModal({ isOpen, onClose, onResetDemo }) {
  const { settings, setSettings } = useSettings();
  const { t } = useLanguage();
  const [resetConfirm, setResetConfirm] = useState(false);

  if (!isOpen) return null;

  const handleThemeChange = (theme) => setSettings({ theme });
  const handleAnimationsChange = (v) => setSettings({ animations: v });
  const handleNotificationsChange = (v) => setSettings({ notifications: v });
  const handleLanguageChange = (lang) => setSettings({ language: lang });

  const handleResetDemo = () => {
    if (!resetConfirm) {
      setResetConfirm(true);
      return;
    }
    onResetDemo?.();
    setResetConfirm(false);
    onClose();
  };

  const normalizedLang = settings.language === 'en' ? 'en-US' : (settings.language || 'pt-BR');

  const modal = (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm animate-fade-in"
      onClick={onClose}
    >
      <div
        className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl shadow-soft-lg w-full max-w-lg max-h-[90vh] overflow-hidden flex flex-col animate-fade-in-up"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 py-5 border-b border-slate-200 dark:border-slate-700 flex-shrink-0">
          <h2 className="text-lg font-semibold text-title">{t.settings}</h2>
          <p className="text-sm text-muted mt-0.5">{t.preferences}</p>
        </div>

        <div className="p-6 overflow-y-auto flex-1 space-y-6">
          {/* Theme */}
          <div>
            <label className="block text-sm font-medium text-muted mb-2">{t.themeMode}</label>
            <div className="flex gap-2">
              <button
                onClick={() => handleThemeChange('light')}
                className={`flex-1 py-2.5 px-4 rounded-lg border font-medium text-sm transition-all ${
                  settings.theme === 'light'
                    ? 'border-cyan-500 bg-cyan-500/15 text-cyan-400'
                    : 'border-slate-200 dark:border-slate-700 text-muted hover:bg-slate-100 dark:hover:bg-slate-700/40'
                }`}
              >
                {t.lightTheme}
              </button>
              <button
                onClick={() => handleThemeChange('dark')}
                className={`flex-1 py-2.5 px-4 rounded-lg border font-medium text-sm transition-all ${
                  settings.theme === 'dark'
                    ? 'border-cyan-500 bg-cyan-500/15 text-cyan-400'
                    : 'border-slate-200 dark:border-slate-700 text-muted hover:bg-slate-100 dark:hover:bg-slate-700/40'
                }`}
              >
                {t.darkTheme}
              </button>
            </div>
          </div>

          {/* Animations */}
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-title">{t.interfaceAnimations}</p>
              <p className="text-xs text-muted">{t.animationDesc}</p>
            </div>
            <button
              onClick={() => handleAnimationsChange(!settings.animations)}
              className={`relative w-12 h-7 rounded-full transition-colors ${
                settings.animations ? 'bg-cyan-500' : 'bg-slate-600'
              }`}
            >
              <span
                className={`absolute top-1 left-1 w-5 h-5 rounded-full bg-slate-200 shadow transition-transform ${
                  settings.animations ? 'translate-x-5' : ''
                }`}
              />
            </button>
          </div>

          {/* Language */}
          <div>
            <label className="block text-sm font-medium text-muted mb-2">{t.language}</label>
            <select
              value={normalizedLang}
              onChange={(e) => handleLanguageChange(e.target.value)}
              className="w-full input-base py-2.5"
            >
              <option value="pt-BR">Português (Brasil)</option>
              <option value="en-US">English (US)</option>
            </select>
          </div>

          {/* Notifications */}
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-title">{t.notificationsSetting}</p>
              <p className="text-xs text-muted">{t.notificationsDesc}</p>
            </div>
            <button
              onClick={() => handleNotificationsChange(!settings.notifications)}
              className={`relative w-12 h-7 rounded-full transition-colors ${
                settings.notifications ? 'bg-cyan-500' : 'bg-slate-600'
              }`}
            >
              <span
                className={`absolute top-1 left-1 w-5 h-5 rounded-full bg-slate-200 shadow transition-transform ${
                  settings.notifications ? 'translate-x-5' : ''
                }`}
              />
            </button>
          </div>

          {/* Demo Tools */}
          <div className="pt-4 border-t border-slate-200 dark:border-slate-700">
            <h3 className="text-sm font-semibold text-title mb-2">{t.demoTools}</h3>
            <p className="text-xs text-muted mb-3">{t.demoToolsDesc}</p>
            <button
              onClick={handleResetDemo}
              className={`py-2.5 px-4 rounded-lg font-medium text-sm transition-colors ${
                resetConfirm
                  ? 'bg-red-600 text-white hover:bg-red-700'
                  : 'bg-amber-500/15 text-amber-400 border border-amber-500/30 hover:bg-amber-500/25'
              }`}
            >
              {resetConfirm ? t.confirmReset : t.resetSimulationData}
            </button>
          </div>
        </div>

        <div className="px-6 py-4 border-t border-slate-200 dark:border-slate-700 flex-shrink-0">
          <button
            onClick={onClose}
            className="w-full py-2.5 px-4 rounded-lg bg-slate-100 dark:bg-slate-800 text-title font-medium hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors"
          >
            {t.close}
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
}
